import { ShipmentEditorState } from './shipment-editor.state';
import { Action, createReducer, on } from '@ngrx/store';
import {
    shipmentEditorAddCommodityFromTemplate,
    shipmentEditorAddCourierOption,
    shipmentEditorAddEmptyCommodity,
    shipmentEditorAddEmptyPackage,
    shipmentEditorAddPackageFromTemplate,
    shipmentEditorChangeDetected,
    shipmentEditorCreateReference,
    shipmentEditorDeleteCommodities,
    shipmentEditorDeleteCommodity,
    shipmentEditorDeletePackage,
    shipmentEditorDeletePackages,
    shipmentEditorDeTemplateCommodity,
    shipmentEditorDeTemplatePackage,
    shipmentEditorDisplayEditor,
    shipmentEditorDisplayQuotes,
    shipmentEditorInvalidate,
    shipmentEditorLoadFailure,
    shipmentEditorLoadRequest,
    shipmentEditorLoadSuccess,
    shipmentEditorMoveCommodities,
    shipmentEditorPatchCommodities, shipmentEditorPatchCustoms,
    shipmentEditorPatchForms,
    shipmentEditorPatchOptions,
    shipmentEditorPatchPackages,
    shipmentEditorPatchReferences,
    shipmentEditorRecordUndo,
    shipmentEditorRedo,
    shipmentEditorRemoveCourierOption,
    shipmentEditorRemoveReference,
    shipmentEditorSaveFailure,
    shipmentEditorSaveRequest,
    shipmentEditorSaveSuccess,
    shipmentEditorSetCategory,
    shipmentEditorSetCurrency,
    shipmentEditorSetInsurance,
    shipmentEditorSetMode,
    shipmentEditorSetOption,
    shipmentEditorSetOptionData,
    shipmentEditorSetPackagingType,
    shipmentEditorSetShipmentDate,
    shipmentEditorSetSignatureOption, shipmentEditorSetValidationMode,
    shipmentEditorSetViolations,
    shipmentEditorSetWeightUnit, shipmentEditorSwapAddresses,
    shipmentEditorUndo,
    shipmentEditorUpdate, shipmentEditorUpdateBroker,
    shipmentEditorUpdateDestination,
    shipmentEditorUpdateOptionsSection,
    shipmentEditorUpdatePackageSection,
    shipmentEditorUpdateShipper, shipmentEditorUpdateSoldTo, shipmentEditorValidateQuotes,
    shipmentEditorDeleteDocumentRequest, shipmentEditorDeleteDocumentSuccess, shipmentEditorDeleteDocumentFailure,
    shipmentEditorBoxPack, shipmentEditorBoxPackSuccess, shipmentEditorBoxPackFailure
} from './shipment-editor.actions';
import { emptyShipment } from '../../constants/empty-shipment';
import { CourierOptionDefinition } from '../../models/courier-option-definition';
import { ReferencesModification } from '../../models/references-modification';
import { cloneShipment } from '../../utils/clone-shipment';
import { normalizeShipment } from '../../utils/normalize-shipment';
import { emptyPackage } from '../../constants/empty-package';
import { emptyCommodity } from '../../constants/empty-commodity';
import { removeAt } from '../../utils/array-reducer';
import { shipmentHash } from '../../utils/shipment-hash';
import { Clock } from '../../utils/clock';
import { normalizePackage } from '../../utils/normalize-package';
import { WeightUnitEnum } from '../../enum/weight-unit.enum';
import { DimensionUnitEnum } from '../../enum/dimension-unit.enum';
import { Commodity } from '../../models/commodity';
import { Package } from '../../models/package';
import { Shipment } from 'src/app/models/shipment';
import { PackagingTypeEnum } from '../../enum/packaging-type.enum';
import { ShipmentCategoryEnum } from '../../enum/shipment-category.enum';
import { SignatureOptionEnum } from '../../enum/signature-option.enum';
import { PackageCodeEnum } from '../../enum/package-code.enum';
import { ValidationMode } from '../../enum/validation-mode.enum';
import { PaymentOptionEnum } from '../../enum/payment-option.enum';
import { Customs } from '../../models/customs';

const initialState: ShipmentEditorState = {
    id: null,
    shipment: emptyShipment,
    undoStack: [],
    redoStack: [],
    ignoreValueHash: '',
    undo: emptyShipment,
    quickQuote: false,
    loading: false,
    saving: false,
    violations: [],
    savingHash: '',
    savedHash: '',
    changedAt: null,
    savedAt: null,
    displayQuotes: false,
    validationMode: ValidationMode.NONE,
    deletingDocumentIds: [],
    boxPackingBusy: false,
    boxPackingSucceeded: false,
    boxPackingFailed: null
};

function addCourierOption(selection: any, definition: CourierOptionDefinition): any {
    const output = { ...selection };
    output[definition.code] = '1';
    for (const key of Object.keys(definition.parameters)) {
        const parameter = definition.parameters[key];
        output[parameter.code] = parameter.default ? parameter.default : '';
    }
    return output;
}

function removeCourierOption(selection: any, definition: CourierOptionDefinition): any {
    const output = { ...selection };
    delete output[definition.code];
    for (const key of Object.keys(definition.parameters)) {
        const parameter = definition.parameters[key];
        delete output[parameter.code];
    }
    return output;
}

function addReference(references: string[], definition: ReferencesModification): any {
    let nextIndex = 1;
    const output = { ...references };
    for (const referencesKey in references) {
        if (references.hasOwnProperty(referencesKey)) {
            const index = parseInt(referencesKey.replace(/[\w]+([0-9]+)$/, '$1'), 10);
            nextIndex = Math.max(nextIndex, index + 1);
        }
    }
    for (const groupKey of Object.keys(definition.grouping)) {
        const grouping = definition.grouping[groupKey];
        const key = grouping.name + nextIndex;
        output[key] = '';
    }
    return output;
}

/**
 * Remove a 1-based reference row and compact higher rows.
 * Works with a "map-like array" where keys look like "ref1", "user2", etc.
 *
 * Example:
 *  removeReference({ ref1:'A', user1:'U1', ref2:'B', user2:'U2' } as any, 1)
 *  => { ref1:'B', user1:'U2' }
 */
function removeReference(references: string[], index: number): string[] {
    if (!references || index < 1) {
        return references;
    }

    const src: any = references;           // map-like array
    const out: any = Array.isArray(references) ? [] : {};
    const rx = /^([^\d]+)(\d+)$/;          // prefix + trailing digits
    let changed = false;

    for (const key in src) {
        if (!Object.prototype.hasOwnProperty.call(src, key)) {
            continue;
        }

        const m = rx.exec(key);
        if (!m) {
            // keep non-suffixed keys as-is
            out[key] = src[key];
            continue;
        }

        const [ , prefix, numStr ] = m;
        const n = parseInt(numStr, 10);

        if (n === index) {
            // drop this row
            changed = true;
            continue;
        }

        if (n > index) {
            // shift rows above the removed one down by 1
            const newKey = `${prefix}${n - 1}`;
            out[newKey] = src[key];
            if (newKey !== key) {
                changed = true;
            }
        } else {
            // rows below the removed index stay the same
            out[key] = src[key];
        }
    }

    return changed ? (out as string[]) : references;
}

function recordUndo(state: ShipmentEditorState): ShipmentEditorState {
    return {
        ...state,
        undoStack: [ { ...state.undo }, ...state.undoStack ],
        redoStack: [],
        undo: cloneShipment(state.shipment),
        ignoreValueHash: shipmentHash(state.shipment)
    };
}

function pullUndo(state: ShipmentEditorState): ShipmentEditorState {
    if (state.undoStack.length > 0) {
        const shipment = { ...state.undoStack[0] };
        return {
            ...state,
            undoStack: [ ...state.undoStack.slice(1) ],
            redoStack: [ { ...state.shipment }, ...state.redoStack.slice(0) ],
            undo: cloneShipment(shipment),
            shipment: cloneShipment(shipment),
            changedAt: Clock.timestamp()
        };
    } else {
        return { ...state };
    }
}

function pullRedo(state: ShipmentEditorState): ShipmentEditorState {
    if (state.redoStack.length > 0) {
        const shipment = { ...state.redoStack[0] };
        return {
            ...state,
            undoStack: [ cloneShipment(state.shipment), ...state.undoStack.slice(0) ],
            redoStack: [ ...state.redoStack.slice(1) ],
            undo: cloneShipment(shipment),
            shipment: cloneShipment(shipment),
            changedAt: Clock.timestamp()
        };
    } else {
        return { ...state };
    }
}

function mergeShipment(existing: Shipment, update: Partial<Shipment>): Shipment {
    return {
        ...existing,
        ...update,
        packages: mergeArrayByIndex(existing.packages, update.packages, mergePackage),
    };
}

function mergePackage(existing: Package, update?: Partial<Package>): Package {
    return {
        ...existing,
        ...normalizePackage(update),
        commodities: mergeArrayByIndex(existing.commodities, update?.commodities, mergeCommodity),
    };
}

function mergeCommodity(existing: Commodity, update?: Partial<Commodity>): Commodity {
    return {
        ...existing,
        ...update,
    };
}

function mergeArrayByIndex<T>(
    existing: T[] = [],
    updates: Partial<T[]> | undefined,
    mergeFn: (a: T, b?: Partial<T>) => T
): T[] {
    const result: T[] = [];
    for (let i = 0; i < Math.max(existing.length, updates?.length || 0); i++) {
        const merged = mergeFn(existing[i], updates?.[i]);
        result.push(merged);
    }
    return result;
}

function moveCommodities(
    packages: Package[],
    indices: number[],
    targetPackageIndex: number
): Package[] {
    if (!packages || packages.length === 0 || indices.length === 0) {
        return packages;
    }

    // Guard: invalid target index -> no-op
    if (targetPackageIndex < 0 || targetPackageIndex >= packages.length) {
        return packages;
    }

    const selectedIndexSet = new Set(indices);
    const selectedCommodities: Commodity[] = [];

    let globalIndex = 0;

    const newPackages: Package[] = packages.map(pkg => ({
        ...pkg,
        commodities: [] as Commodity[]
    }));

    // Walk all commodities in flattened order, keeping or selecting
    packages.forEach((pkg, pkgIndex) => {
        const newCommodities: Commodity[] = [];

        pkg.commodities.forEach((commodity) => {
            if (selectedIndexSet.has(globalIndex)) {
                // This commodity is being moved
                selectedCommodities.push(commodity);
            } else {
                // Keep it in its current package
                newCommodities.push(commodity);
            }
            globalIndex++;
        });

        newPackages[pkgIndex] = {
            ...newPackages[pkgIndex],
            commodities: newCommodities
        };
    });

    if (selectedCommodities.length === 0) {
        return packages; // nothing to move
    }

    // Append moved commodities to the target package
    const targetPkg = newPackages[targetPackageIndex];

    newPackages[targetPackageIndex] = {
        ...targetPkg,
        commodities: [
            ...targetPkg.commodities,
            ...selectedCommodities
        ]
    };

    return newPackages;
}

function patchCommoditiesByGlobalIndex(
    packages: Package[],
    indices: number[],
    com: Partial<Commodity>
): Package[] {
    if (!packages || packages.length === 0 || indices.length === 0) {
        return packages;
    }

    const indexSet = new Set(indices);
    let globalIndex = 0;
    let anyPatched = false;

    const newPackages = packages.map(pkg => {
        const newCommodities = pkg.commodities.map(commodity => {
            const shouldPatch = indexSet.has(globalIndex);
            globalIndex++;

            if (!shouldPatch) {
                return commodity;
            }

            anyPatched = true;
            return {
                ...commodity,
                ...com,
            };
        });

        return {
            ...pkg,
            commodities: newCommodities,
        };
    });

    // If nothing matched, just return the original array to avoid unnecessary churn
    return anyPatched ? newPackages : packages;
}

function deleteCommoditiesByGlobalIndex(
    packages: Package[],
    indices: number[]
): Package[] {
    if (!packages || packages.length === 0 || indices.length === 0) {
        return packages;
    }

    const indexSet = new Set(indices);
    let globalIndex = 0;
    let anyDeleted = false;

    const newPackages = packages.map(pkg => {
        const kept: Commodity[] = [];

        for (const commodity of pkg.commodities) {
            const shouldDelete = indexSet.has(globalIndex);
            globalIndex++;

            if (shouldDelete) {
                anyDeleted = true;
                continue; // skip this commodity
            }

            kept.push(commodity);
        }

        return {
            ...pkg,
            commodities: kept,
        };
    });

    // If nothing was deleted, return the original reference to avoid useless churn
    return anyDeleted ? newPackages : packages;
}

function patchOptions(options: any[string], optionId: string, value: boolean | string): any[string] {
    if (value === false) {
        const output = { ...options };
        delete output[optionId];
        return output;
    }
    return {
        ...options,
        [optionId]: value === true ? '1' : value
    };
}

function updateValidationMode(current: ValidationMode, next: ValidationMode): ValidationMode
{
    if (next === ValidationMode.QUOTE) {
        return current === ValidationMode.NONE ? ValidationMode.QUOTE : current;
    }
    return next;
}

function createEmptyPackage(s: Shipment): Package {
    const pkg = { ...emptyPackage, commodities: [ { ...emptyCommodity } ] };
    switch (s.packagingType) {
        case PackagingTypeEnum.PALLET:
            pkg.length = s.dimensionUnit === 'CM' ? 120 : 48;
            pkg.width = s.dimensionUnit === 'CM' ? 80 : 40;
            pkg.height = s.dimensionUnit === 'CM' ? 80 : 40;
            pkg.packageCode = PackageCodeEnum.PALLET;
            pkg.stackable = false;
            break;
        case PackagingTypeEnum.CUSTOMER_PACKAGING:
            pkg.packageCode = PackageCodeEnum.BOX;
            break;
    }
    return pkg;
}

const reducer = createReducer(
    initialState,
    on(shipmentEditorLoadRequest, (state, { id }) => ({ ...state, id, shipment: { ...state.shipment }, loading: true })),
    on(shipmentEditorLoadSuccess, (state, { shipment }) => ({
        ...state,
        id: shipment.id,
        shipment: { ...shipment },
        undo: { ...shipment },
        loading: false,
        savedHash: shipmentHash(shipment),
        changedAt: null,
        savedAt: null,
        ignoreValueHash: shipmentHash(shipment),
        validationMode: ValidationMode.NONE,
    })),
    on(shipmentEditorLoadFailure, (state) => ({ ...state, loading: false })),
    on(shipmentEditorBoxPack, (state) => ({
        ...state,
        boxPackingBusy: true,
        boxPackingSucceeded: false,
        boxPackingFailed: null
    })),
    on(shipmentEditorBoxPackSuccess, (state) => ({
        ...state,
        boxPackingBusy: false,
        boxPackingSucceeded: true,
        boxPackingFailed: null
    })),
    on(shipmentEditorBoxPackFailure, (state, { error }) => ({
        ...state,
        boxPackingBusy: false,
        boxPackingSucceeded: false,
        boxPackingFailed: error
    })),
    on(shipmentEditorSaveRequest, (state) => ({ ...state, saving: true, savingHash: shipmentHash(state.shipment) })),
    on(shipmentEditorSaveSuccess, (state) => ({
        ...state,
        saving: false,
        savedHash: state.savingHash,
        savingHash: '',
        changedAt: null,
        savedAt: Clock.timestamp()
    })),
    on(shipmentEditorSaveFailure, (state) => ({ ...state, saving: false, savingHash: '' })),
    on(shipmentEditorAddCourierOption, (state, { option }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            courierOptions: addCourierOption(state.shipment.courierOptions, option)
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorRemoveCourierOption, (state, { option }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            courierOptions: removeCourierOption(state.shipment.courierOptions, option)
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorCreateReference, (state, { modification }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            references: addReference(state.shipment.references, modification)
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorRemoveReference, (state, { index }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            references: removeReference(state.shipment.references, index)
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorUpdate, (state, { shipment }) => ({
        ...state,
        shipment: {
            id: state.shipment.id,
            ...normalizeShipment(cloneShipment(shipment))
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorSetShipmentDate, (state, { shipmentDate }) => (
        { ...state, shipment: { ...state.shipment, shipmentDate }, changedAt: Clock.timestamp() })
    ),
    on(shipmentEditorRecordUndo, (state) => (recordUndo(state))),
    on(shipmentEditorUndo, (state) => (pullUndo(state))),
    on(shipmentEditorRedo, (state) => (pullRedo(state))),
    on(shipmentEditorInvalidate, (state) => ({ ...state, id: null, ignoreValueHash: '' })),
    on(shipmentEditorSetMode, (state, { quickQuote }) => ({
        ...state, shipment: cloneShipment(state.shipment), quickQuote
    })),
    on(shipmentEditorSetViolations, (state, { violations }) => ({
        ...state,
        violations
    })),
    on(shipmentEditorDisplayQuotes, (state) => ({
        ...state,
        displayQuotes: true
    })),
    on(shipmentEditorDisplayEditor, (state) => ({
        ...state,
        displayQuotes: false
    })),
    on(shipmentEditorSetOptionData, (state, { courierOptions }) => ({
        ...state,
        shipment: { ...state.shipment, courierOptions },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorUpdateShipper, (state, { card }) => ({
        ...state,
        shipment: { ...state.shipment, shipper: { ...state.shipment.shipper, ...card } },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorUpdateDestination, (state, { card }) => ({
        ...state,
        shipment: { ...state.shipment, destination: { ...state.shipment.destination, ...card } },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorUpdateBroker, (state, { card }) => ({
        ...state,
        shipment: { ...state.shipment, broker: { ...state.shipment.broker, ...card } },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorUpdateSoldTo, (state, { card }) => ({
        ...state,
        shipment: { ...state.shipment, soldTo: { ...state.shipment.soldTo, ...card } },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorUpdatePackageSection, (state, { documentsOnly, packagingType, weightUnit, currency, packages }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            documentsOnly,
            packagingType,
            weightUnit,
            dimensionUnit: weightUnit === 'KG' ? 'CM' : 'IN',
            currency,
            packages: mergeArrayByIndex(state.shipment.packages, packages, mergePackage)
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorAddEmptyPackage, (state) => ({
        ...state,
        shipment: { ...state.shipment, packages: [ ...state.shipment.packages, createEmptyPackage(state.shipment) ] },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorAddPackageFromTemplate, (state, { template }) => ({
        ...state,
        shipment: { ...state.shipment, packages: [ ...state.shipment.packages, { ...template, id: null, templateId: template.id } ] },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorDeletePackage, (state, { packageIndex }) => ({
        ...state,
        shipment: { ...state.shipment, packages: removeAt(state.shipment.packages, packageIndex) },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorDeletePackages, (state, { packageIndices }) => ({
        ...state,
        shipment: { ...state.shipment, packages: state.shipment.packages.filter((_, i) => !packageIndices.includes(i)) },
    })),
    on(shipmentEditorAddEmptyCommodity, (state, { packageIndex }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            packages: [
                ...state.shipment.packages.map((p, i) => i === packageIndex ? {
                    ...p,
                    commodities: [ ...p.commodities, emptyCommodity ]
                } : p)
            ]
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorAddCommodityFromTemplate, (state, { packageIndex, template }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            packages: [
                ...state.shipment.packages.map((p, i) => i === packageIndex ? {
                    ...p,
                    commodities: [ ...p.commodities, { ...template, id: null, templateId: template.id } ]
                } : p)
            ]
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorDeleteCommodity, (state, { packageIndex, commodityIndex }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            packages: [
                ...state.shipment.packages.map((p, i) => i === packageIndex ? {
                    ...p,
                    commodities: removeAt(p.commodities, commodityIndex)
                } : p)
            ]
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorUpdateOptionsSection, (state, { shipmentDate, paymentOption, paymentAccountNumber, signatureOption }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            shipmentDate,
            paymentOption,
            paymentAccountNumber,
            courierOptions: { ...state.shipment.courierOptions, signature_required: signatureOption }
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorPatchOptions, (state, { options }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            courierOptions: {
                ...state.shipment.courierOptions,
                ...options
            }
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorPatchReferences, (state, { references }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            references: {
                ...state.shipment.references,
                ...references
            }
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorPatchForms, (state, { forms }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            courierForms: {
                ...state.shipment.courierForms,
                ...forms
            }
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorSetInsurance, (state, { insurance }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            packages: state.shipment.packages.map(p => ({
                ...p,
                value: insurance ? Math.max(1, p.value) : 0
            }))
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorSetPackagingType, (state, { packagingType }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            packagingType
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorSetWeightUnit, (state, { weightUnit }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            weightUnit,
            dimensionUnit: weightUnit === WeightUnitEnum.KG ? DimensionUnitEnum.CM : DimensionUnitEnum.IN
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorSetSignatureOption, (state, { signatureOption }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            signatureOption
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorSetCurrency, (state, { currency }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            currency
        },
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorChangeDetected, (state) => ({
        ...state,
        ignoreValueHash: shipmentHash(state.shipment),
        changedAt: Clock.timestamp()
    })),
    on(shipmentEditorDeTemplatePackage, (state, { packageIndex }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            packages: state.shipment.packages.map((p, index) => ({ ...p, templateId: index === packageIndex ? null : p.templateId }))
        }
    })),
    on(shipmentEditorDeTemplateCommodity, (state, { packageIndex, commodityIndex }) => ({
        ...state,
        shipment: {
            ...state.shipment,
            packages: state.shipment.packages.map((p, index1) => ({
                ...p, commodities: p.commodities.map((c, index2) => ({
                    ...c, templateId: (index1 === packageIndex && index2 === commodityIndex) ? null : c.templateId
                }))
            }))
        }
    })),
    on(shipmentEditorSetCategory, (state, { category }) => ({
        ...state,
        changedAt: Clock.timestamp(),
        shipment: {
            ...state.shipment,
            signatureOption: category === ShipmentCategoryEnum.LTL ? SignatureOptionEnum.DEFAULT : state.shipment.signatureOption,
            packagingType: category === ShipmentCategoryEnum.LTL ? PackagingTypeEnum.PALLET :
                state.shipment.packagingType === PackagingTypeEnum.PALLET ?
                    PackagingTypeEnum.CUSTOMER_PACKAGING : state.shipment.packagingType,
            paymentOption: category === ShipmentCategoryEnum.LTL ? PaymentOptionEnum.ACCOUNT : state.shipment.paymentOption,
            paymentAccountNumber: category === ShipmentCategoryEnum.LTL ? '' : state.shipment.paymentAccountNumber,
        }
    })),
    on(shipmentEditorPatchPackages, (state, { indices, pkg }) => ({
        ...state,
        changedAt: Clock.timestamp(),
        shipment: {
            ...state.shipment,
            packages: state.shipment.packages
                .map((p, index) => indices.includes(index) ? { ...p, ...pkg } : p)
        }
    })),
    on(shipmentEditorMoveCommodities, (state, { indices, targetPackageIndex }) => ({
        ...state,
        changedAt: Clock.timestamp(),
        shipment: {
            ...state.shipment,
            packages: moveCommodities(
                state.shipment.packages,
                indices,
                targetPackageIndex
            ),
        },
    })),
    on(shipmentEditorPatchCommodities, (state, { indices, com }) => ({
        ...state,
        changedAt: Clock.timestamp(),
        shipment: {
            ...state.shipment,
            packages: patchCommoditiesByGlobalIndex(
                state.shipment.packages,
                indices,
                com
            ),
        },
    })),
    on(shipmentEditorDeleteCommodities, (state, { indices }) => ({
        ...state,
        changedAt: Clock.timestamp(),
        shipment: {
            ...state.shipment,
            packages: deleteCommoditiesByGlobalIndex(
                state.shipment.packages,
                indices
            ),
        },
    })),
    on(shipmentEditorSetOption, (state, { option, value }) => ({
        ...state,
        changedAt: Clock.timestamp(),
        shipment: {
            ...state.shipment,
            courierOptions: patchOptions(state.shipment.courierOptions, option, value)
        }
    })),
    on(shipmentEditorSetValidationMode, (state, { mode }) => ({
        ...state,
        validationMode: updateValidationMode(state.validationMode, mode),
    })),
    on(shipmentEditorValidateQuotes, (state) => ({
        ...state,
        validationMode: state.validationMode === ValidationMode.SUBMIT ? ValidationMode.SUBMIT : ValidationMode.QUOTE
    })),
    on(shipmentEditorPatchCustoms, (state, { customs }) => ({
        ...state,
        changedAt: Clock.timestamp(),
        shipment: {
            ...state.shipment,
            customs: {
                ...state.shipment.customs,
                ...customs
            }
        }
    })),
    on(shipmentEditorSwapAddresses, (state) => ({
        ...state,
        shipment: {
            ...state.shipment,
            shipper: { ...state.shipment.destination },
            destination: { ...state.shipment.shipper }
        }
    })),
    on(shipmentEditorDeleteDocumentRequest, (state, { documentId }) => ({
        ...state,
        deletingDocumentIds: [ ...(state.deletingDocumentIds ?? []), documentId ]
    })),
    on(shipmentEditorDeleteDocumentSuccess, (state, { documentId }) => ({
        ...state,
        deletingDocumentIds: (state.deletingDocumentIds ?? []).filter(id => id !== documentId),
        shipment: {
            ...state.shipment,
            documents: (state.shipment.documents ?? []).filter(d => d.id !== documentId)
        }
    })),
    on(shipmentEditorDeleteDocumentFailure, (state, { documentId }) => ({
        ...state,
        deletingDocumentIds: (state.deletingDocumentIds ?? []).filter(id => id !== documentId)
    })),
);

export function shipmentEditorReducer(state: ShipmentEditorState | undefined, action: Action): any {
    return reducer(state, action);
}
